// Ad gate core: decides whether a gated action (print/export/…) may proceed,
// and persists the "ad-free until" reward window. UI-layer only — the export
// primitives stay ungated (frozen __olm test surface calls them directly).
//
// Fail-open philosophy (mirrors checkBatchUsage in billing.ts): anything
// unexpected — modal module missing, storage errors, vendor outages — resolves
// 'allow'. Detected adblock is the one exception; that policy lives in the
// modal (see panels/adGateModal.ts).
import { state } from './../state'
import { effectiveTier } from './../auth/org'
import { supabase, tierSettled } from './../auth/supabase'
import { adsConfig } from './config'
import { trackEvent } from './../analytics'

export type GateAction = 'print' | 'export' | 'batch-export' | 'olm-download' | 'mobile-export'
export type GateVerdict = 'allow' | 'deny'

const LS_KEY = 'olm_ad_free_until'

/** Epoch ms until which the user is ad-free. Max of local + roaming stores. */
export function adFreeUntil(): number {
  let local = 0
  try { local = parseInt(localStorage.getItem(LS_KEY) ?? '0', 10) || 0 } catch { /* storage blocked */ }
  const roaming = Number(state.currentUser?.user_metadata?.ad_free_until) || 0
  return Math.max(local, roaming)
}

/** Persist the ad-free window: localStorage always; user_metadata when logged in (fire-and-forget). */
export function setAdFreeUntil(ts: number): void {
  try { localStorage.setItem(LS_KEY, String(ts)) } catch { /* storage blocked — roaming store still works */ }
  if (state.currentUser) {
    void supabase.auth.updateUser({ data: { ad_free_until: ts } }).catch(() => { /* grant already applied locally */ })
  }
}

/** True when the current user should see the ad gate right now. */
export function gateApplies(): boolean {
  const cfg = adsConfig()
  if (!cfg.enabled || cfg.audience === 'off') return false
  if (adFreeUntil() > Date.now()) return false
  if (!state.currentUser) return true // anonymous: gated under both audiences
  if (cfg.audience === 'anon') return false
  return effectiveTier() === 'free'
}

let inFlight: Promise<GateVerdict> | null = null

/**
 * Resolve whether `action` may proceed. Instant 'allow' when the gate does not
 * apply (no modal churn); otherwise shows the ad-gate modal and resolves from
 * the user's choice. Re-entrant calls share the in-flight promise.
 */
export async function ensureAdGate(action: GateAction): Promise<GateVerdict> {
  try {
    // Central funnel for every print/export attempt — gated or not — so usage
    // tracking lives here instead of at each of the six chokepoints.
    trackEvent('gated_action', { action, gated: gateApplies() })
    if (!gateApplies()) return 'allow'
    // 'anon+free' inspects tier, which loads async after boot — wait briefly so
    // a business user is never gated by a transient 'free' (then re-check).
    if (adsConfig().audience === 'anon+free' && state.currentUser) {
      await Promise.race([tierSettled, new Promise((r) => setTimeout(r, 2500))])
      if (!gateApplies()) return 'allow'
    }
    if (inFlight) return inFlight
    inFlight = (async () => {
      const { openAdGate } = await import('./../panels/adGateModal')
      return openAdGate(action)
    })().catch(() => 'allow' as GateVerdict)
    try {
      const v = await inFlight
      trackEvent('adgate_verdict', { action, verdict: v })
      return v
    } finally { inFlight = null }
  } catch {
    return 'allow' // never block printing on a gate malfunction
  }
}

/** Wrap an action callback in the gate; returns a fire-and-forget click handler. */
export function gated(action: GateAction, run: () => void | Promise<void>): () => void {
  return () => {
    void (async () => {
      if (await ensureAdGate(action) === 'allow') await run()
    })()
  }
}
